import Dialog from "../../General/ConfirmableDialog";
import { FC, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { DataGrid, GridRowModel, GridValidRowModel } from "@mui/x-data-grid";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { IRepairsTableProps } from "../../../interfaces/Fleet/IRepairsTableProps";
import { createRepairsTableColumns } from "../../../data/FormColumns/Fleet/repairsTableColumns";
import RepairPopup from "../RepairPopup/RepairPopup";
import { getRepairRowClassName } from "../../../helpers/getRowClassName";
import FileUploadModal from "../FileUploadModal/FileUploadModal";
import { apiVariable } from "../../../api/apiVariable";
import useAuth from "../../../hooks/useAuth";
import ShopDetailsPopup from "../ShopDetailsPopup/ShopDetailsPopup";
import { IShopDetails } from "../../../interfaces/Fleet/IShopDetails";
import StatusSnackbar from "../../General/StatusSnackbar/StatusSnackbar";
import IncidentProofUploadModal from "../IncidentProofUploadModal/IncidentProofUploadModal";
import * as signalR from "@microsoft/signalr";
import GenericFormPopup from "../GenericFormPopup/GenericFormPopup";
import { getUpdateIncidentFromRepairTableFieldConfiguration } from "../../../formConfigs/Fleet/incidentFieldConfig";
import { getIncidentTypesForAsset } from "../../../data/Fleet/IncidentType";
import { capitalizeFirstChar } from "../../../helpers/formatingFunctions";
import { OptionType } from "../../../interfaces/Fleet/IGenericFormPopupProps";
import { fetchOwnerCompanies } from "../TruckPopup/TruckPopup";
import AiRepairSplitsDialog from "../AiRepairSplitsDialog/AiRepairSplitsDialog";

const normalizeLocationValue = (location: string) =>
  location.toLowerCase() === "unknown" ? "Unknown" : location.toUpperCase();

interface IncidentChargeDto {
  isCompanyCharged: boolean | null;
  chargedCompanyAmount: number | null;
  isDriverCharged: boolean | null;
  chargedDriverAmount: number | null;
  chargedCompanyId?: string | null;
  chargedCompanyName?: string | null;
}

const chargeIncident = async (
  incidentId: string,
  token: string | null | undefined,
  chargeDto: IncidentChargeDto,
) => {
  const { data } = await axios.put(
    `${apiVariable}/chargeIncident/${incidentId}`,
    chargeDto,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    },
  );
  return data;
};

const RepairsTable: FC<IRepairsTableProps> = ({
  incident,
  open,
  setOpen,
  affectedTruckId,
  affectedTrailerId,
  isIncidentCharged,
}) => {
  const [isNewRepair, setIsNewRepair] = useState(false);
  const [reason, setReason] = useState("");
  const [isReasonModalOpen, setIsReasonModalOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState<GridRowModel | null>(null);
  const [isInvoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [isReceiptModalOpen, setReceiptModalOpen] = useState(false);
  const [isShopDetailsModalOpen, setIsShopDetailsModalOpen] = useState(false);
  const [shopDetails, setShopDetails] = useState<IShopDetails | null>(null);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDriverCharged, setIsDriverCharged] = useState(
    typeof incident?.isDriverCharged === "boolean"
      ? incident.isDriverCharged
      : isIncidentCharged,
  );
  const [isCompanyCharged, setIsCompanyCharged] = useState(
    typeof incident?.isCompanyCharged === "boolean"
      ? incident.isCompanyCharged
      : false,
  );
  const [chargedDriverAmount, setChargedDriverAmount] = useState(
    incident?.chargedDriverAmount
      ? String(incident.chargedDriverAmount)
      : incident?.chargedAmount
        ? String(incident.chargedAmount)
        : "",
  );
  const [chargedCompanyAmount, setChargedCompanyAmount] = useState(
    incident?.chargedCompanyAmount ? String(incident.chargedCompanyAmount) : "",
  );
  const getCompanyFromIncident = (currentIncident = incident) => {
    if (currentIncident?.chargedCompanyId) {
      return {
        id: currentIncident.chargedCompanyId.toString(),
        label: currentIncident.chargedCompanyName || "",
      } as OptionType;
    }

    if (currentIncident?.rentCompanyId) {
      return {
        id: currentIncident.rentCompanyId.toString(),
        label: currentIncident.rentCompanyName || "",
      } as OptionType;
    }

    return null;
  };

  const [selectedCompany, setSelectedCompany] = useState<OptionType | null>(
    getCompanyFromIncident(),
  );
  const [isChargeSubmitting, setIsChargeSubmitting] = useState(false);
  const [completedBy, setCompletedBy] = useState<string>("");
  const [isCompletionModalOpen, setCompletionModalOpen] = useState(false);
  const [completionAuthorizedBy, setCompletionAuthorizedBy] = useState("");
  const [completionFinalCost, setCompletionFinalCost] = useState("");
  const [isUpdateIncidentModalOpen, setIsUpdateIncidentModalOpen] =
    useState(false);
  const [initialValues, setInitialValues] = useState<any>({});
  const [incidentData, setIncidentData] = useState(incident);
  const [isSplitIncidentModalOpen, setIsSplitIncidentModalOpen] =
    useState(false);
  const [splitFinalCost, setSplitFinalCost] = useState("");
  const [complaint, setComplaint] = useState("");
  const [splitIncidentType, setSplitIncidentType] = useState("");
  const [isProofUploadModalOpen, setProofUploadModalOpen] = useState(false);
  const [aiSplitRepair, setAiSplitRepair] = useState<any>(null);
  const incidentTruckId =
    incidentData?.truckId ?? incident?.truckId ?? affectedTruckId;
  const incidentTrailerId =
    incidentData?.trailerId ?? incident?.trailerId ?? affectedTrailerId;
  const incidentAssetType = incidentTrailerId ? "trailer" : "truck";
  const incidentAssetAffected =
    incidentAssetType === "trailer" ? "Trailer" : "Truck";

  const queryClient = useQueryClient();
  const { token } = useAuth();

  useEffect(() => {
    setIncidentData(incident);
  }, [incident]);

  const { data: ownerCompanies } = useQuery({
    queryKey: ["ownerCompanies"],
    queryFn: () => fetchOwnerCompanies(token),
    enabled: open && !!token,
  });

  const resetChargeState = () => {
    setIsDriverCharged(
      typeof incident?.isDriverCharged === "boolean"
        ? incident.isDriverCharged
        : isIncidentCharged,
    );
    setIsCompanyCharged(
      typeof incident?.isCompanyCharged === "boolean"
        ? incident.isCompanyCharged
        : false,
    );
    setChargedDriverAmount(
      incident?.chargedDriverAmount
        ? String(incident.chargedDriverAmount)
        : incident?.chargedAmount
          ? String(incident.chargedAmount)
          : "",
    );
    setChargedCompanyAmount(
      incident?.chargedCompanyAmount
        ? String(incident.chargedCompanyAmount)
        : "",
    );
    setSelectedCompany(getCompanyFromIncident());
  };

  useEffect(() => {
    resetChargeState();
  }, [incident, isIncidentCharged]);

  const handleSnackbarClose = () => setStatus("idle");

  const fetchRepairs = async () => {
    const { data: repairsData } = await axios.get(
      `${apiVariable}/repair/${incident.id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const filesAvailability = await Promise.all(
      repairsData.map((repair: { id: any }) =>
        axios
          .get(`${apiVariable}/repairFiles/${repair.id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((res) => ({ repairId: repair.id, availability: res.data })),
      ),
    );

    const availabilityMap = filesAvailability.reduce((acc, current) => {
      acc[current.repairId] = current.availability;
      return acc;
    }, {});

    return repairsData.map((repair: { id: string | number }) => ({
      ...repair,
      filesAvailability: availabilityMap[repair.id],
    }));
  };

  useEffect(() => {
    const repairConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiVariable}/repairHub`, {
        accessTokenFactory: () => token || "",
        withCredentials: false,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.None)
      .build();

    repairConnection.on("RepairUpdated", () => {
      queryClient.invalidateQueries({ queryKey: ["repairs"] });
    });

    repairConnection.start();

    return () => {
      repairConnection.stop();
    };
  }, [token, queryClient]);

  const {
    data: repairs,
    refetch,
    isError,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["repairs", incident.id],
    queryFn: fetchRepairs,
    enabled: !!incident.id && open,
  });

  const chargeSwitchDisabled =
    repairs?.some(
      (repair: { repairStatus: string }) => repair.repairStatus !== "completed",
    ) || !repairs?.length;

  const updateRepairMutation = useMutation({
    mutationFn: (updatedRow: GridRowModel) =>
      axios.put(`${apiVariable}/repair/${updatedRow.id}`, updatedRow, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      refetch();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Update error");
    },
  });

  const updateRepairFileMutation = useMutation({
    mutationFn: (repairFileDto: {
      id: string | number;
      invoiceDate?: string;
      invoiceNumber?: string;
    }) =>
      axios.put(`${apiVariable}/repairFile`, repairFileDto, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      refetch();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response?.data || "Failed to update invoice data");
    },
  });

  const sendReasonMutation = useMutation({
    mutationFn: (reasonData: { id: string | number; reason: string }) =>
      axios.patch(
        `${apiVariable}/onHoldReason/${reasonData.id}`,
        {
          onHoldReason: reasonData.reason,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ),
    onSuccess: () => {
      setStatus("success");
      setIsReasonModalOpen(false);
      setReason("");
      refetch();
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Error sending reason");
    },
  });

  const handleNewRepair = () => {
    setIsNewRepair(true);
  };

  const handleClose = () => {
    setOpen(false);
    resetChargeState();
  };

  const handleDriverChargeToggle = (checked: boolean) => {
    setIsDriverCharged(checked);
    if (!checked) {
      setChargedDriverAmount("");
    }
  };

  const handleCompanyChargeToggle = (checked: boolean) => {
    setIsCompanyCharged(checked);
    if (!checked) {
      setChargedCompanyAmount("");
    }
  };

  const handleChargeSubmit = async () => {
    if (!incident?.id) {
      setStatus("error");
      setErrorMessage("Unable to update charges for this incident.");
      return;
    }

    const amountRegex = /^\d+(\.\d{1,2})?$/;
    if (isDriverCharged && !amountRegex.test(chargedDriverAmount)) {
      setStatus("error");
      setErrorMessage(
        "Enter a valid driver charge amount using a dot and up to two decimals.",
      );
      return;
    }

    if (isCompanyCharged) {
      if (!selectedCompany) {
        setStatus("error");
        setErrorMessage("Select a company to charge.");
        return;
      }

      if (!amountRegex.test(chargedCompanyAmount)) {
        setStatus("error");
        setErrorMessage(
          "Enter a valid company charge amount using a dot and up to two decimals.",
        );
        return;
      }
    }

    const payload: IncidentChargeDto = {
      isDriverCharged: isDriverCharged,
      chargedDriverAmount: isDriverCharged
        ? parseFloat(chargedDriverAmount)
        : null,
      isCompanyCharged: isCompanyCharged,
      chargedCompanyAmount: isCompanyCharged
        ? parseFloat(chargedCompanyAmount)
        : null,
      chargedCompanyId:
        isCompanyCharged && selectedCompany ? selectedCompany.id : null,
      chargedCompanyName:
        isCompanyCharged && selectedCompany ? selectedCompany.label : null,
    };

    setIsChargeSubmitting(true);
    setErrorMessage(null);
    try {
      await chargeIncident(incident.id, token, payload);
      setStatus("success");
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      queryClient.invalidateQueries({ queryKey: ["repairs"] });
      setIncidentData((prev: any) =>
        prev
          ? {
              ...prev,
              isDriverCharged: payload.isDriverCharged,
              isCompanyCharged: payload.isCompanyCharged,
              chargedDriverAmount: payload.chargedDriverAmount,
              chargedCompanyAmount: payload.chargedCompanyAmount,
              chargedCompanyId: payload.chargedCompanyId,
              chargedCompanyName: payload.chargedCompanyName,
            }
          : prev,
      );
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(
        error.response?.data || "Failed to update the incident charges",
      );
    } finally {
      setIsChargeSubmitting(false);
    }
  };

  const openUpdateIncidentModal = () => {
    setIsUpdateIncidentModalOpen(true);
  };

  const closeUpdateIncidentModal = () => {
    setIsUpdateIncidentModalOpen(false);
    currentRow && openInvoiceModal(currentRow);
  };

  const openInvoiceModal = (row: GridValidRowModel) => {
    setCurrentRow(row);
    setInvoiceModalOpen(true);
  };

  const openReceiptModal = (row: GridValidRowModel) => {
    setCurrentRow(row);
    setReceiptModalOpen(true);
  };

  const handleInvoiceClose = (row: GridValidRowModel) => {
    setInvoiceModalOpen(false);
    openReceiptModal(row);
  };

  const mutateRowAfterUpdate = async (updatedRow: GridRowModel) => {
    const response = await updateRepairMutation.mutateAsync(updatedRow);
    queryClient.invalidateQueries({ queryKey: ["incidents"] });
    return response.data;
  };

  const openCompletionModal = (row: GridValidRowModel) => {
    setCurrentRow(row);
    setCompletionAuthorizedBy(row.authorizedBy || "");
    setCompletionFinalCost(row.finalCost ? String(row.finalCost) : "");
    setCompletionModalOpen(true);
  };

  const handleProcessRowUpdate = async (
    newRow: GridRowModel,
    oldRow: GridRowModel,
  ) => {
    try {
      const invoiceDateChanged = newRow.invoiceDate !== oldRow.invoiceDate;
      const invoiceNumberChanged =
        newRow.invoiceNumber !== oldRow.invoiceNumber;
      const hasInvoiceChanges = invoiceDateChanged || invoiceNumberChanged;

      if (hasInvoiceChanges) {
        await updateRepairFileMutation.mutateAsync({
          id: newRow.invoiceId,
          invoiceDate: newRow.invoiceDate,
          invoiceNumber: newRow.invoiceNumber,
        });
      }

      if (newRow.repairStatus === "OnHold") {
        setCurrentRow(newRow);
        setIsReasonModalOpen(true);
        await refetch();
        return await mutateRowAfterUpdate(newRow);
      } else if (newRow.repairStatus === "Completed") {
        newRow.completedBy = completedBy;
        openCompletionModal(newRow);
        await refetch();
        return await mutateRowAfterUpdate(newRow);
      } else {
        newRow.completedBy = null;
        return await mutateRowAfterUpdate(newRow);
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : String(error));
      throw error;
    }
  };

  const handleProcessRowUpdateError = (error: any) => {
    setStatus("error");
    setErrorMessage(error.message || "Error updating row");
  };

  const handleReasonSubmit = () => {
    if (currentRow) {
      sendReasonMutation.mutate({ id: currentRow.id, reason });
    }
  };

  const handleShopNameClick = (details: IShopDetails) => {
    setShopDetails(details);
    setIsShopDetailsModalOpen(true);
  };

  const handleCompletionSubmit = () => {
    const amountRegex = /^\d+(\.\d{1,2})?$/;
    if (!amountRegex.test(completionFinalCost)) {
      setErrorMessage(
        "Invalid amount. Please enter a float number with a maximum of two decimal places.",
      );
      return;
    }

    const updatedRow = {
      ...currentRow,
      authorizedBy: completionAuthorizedBy,
      finalCost: completionFinalCost,
      completedBy,
    };

    mutateRowAfterUpdate(updatedRow);
    setCompletionModalOpen(false);
    setProofUploadModalOpen(true);
    openUpdateIncidentModal();
  };

  const closeCompletionModal = () => {
    setCompletionModalOpen(false);
    setErrorMessage(null);
  };

  useEffect(() => {
    if (open) {
      const formattedCoordinates = incident.coordinates
        .replace(/(\d+),(\d+)/g, "$1.$2")
        .replace(/[()]/g, "");

      setInitialValues({
        assetAffected: incidentAssetAffected,
        assetNumber:
          incidentAssetType === "trailer" ? incidentTrailerId : incidentTruckId,
        driverId: incidentData?.driverId ?? incident.driverId,
        openDate: incident.openDate.split("T")[0],
        complaint: incident.complaint,
        receivedBy: incident.receivedBy,
        location: normalizeLocationValue(incident.location),
        incidentType: capitalizeFirstChar(incident.incidentType),
        coordinates:
          incident.coordinates === "(, )" ? "" : formattedCoordinates,
      });
    }
  }, [
    affectedTrailerId,
    incident,
    incidentAssetAffected,
    incidentAssetType,
    incidentData?.driverId,
    incidentTrailerId,
    incidentTruckId,
    open,
  ]);

  const incidentMutation = useMutation({
    mutationFn: (data: any) =>
      axios.put(`${apiVariable}/updateIncident`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: async () => {
      setStatus("success");
      closeUpdateIncidentModal();

      const response = await axios.get(
        `${apiVariable}/getIncidentById/${incident.id}/${currentRow?.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setIncidentData(response.data);

      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    },
    onError: (error: any) => {
      setStatus("error");
      setErrorMessage(error.response.data || "Failed to submit incident");
    },
  });

  const submitHandler = (data: any) => {
    const requiredFields = ["complaint", "location", "incidentType"];

    const missingFields = requiredFields.filter((field) => !data[field]);

    if (missingFields.length > 0) {
      setStatus("error");
      setErrorMessage("Please fill in all fields. Coordinates are optional.");
      return;
    }

    const coordinates = data.coordinates
      ? data.coordinates.split(",").map((coord: string) => coord.trim())
      : [null, null];
    const [latitude, longitude] = coordinates;

    const submitData = {
      ...data,
      id: incident.id,
      assetAffected: incidentAssetAffected,
      assetNumber:
        incidentAssetType === "trailer" ? incidentTrailerId : incidentTruckId,
      driverId: incidentData?.driverId ?? incident.driverId,
      latitude,
      longitude,
    };

    delete submitData.coordinates;

    incidentMutation.mutate(submitData);
    currentRow && openInvoiceModal(currentRow);
  };

  const splitIncidentMutation = useMutation({
    mutationFn: (data: { finalCost: number; incidentType: string }) =>
      axios.post(`${apiVariable}/splitIncident/${incident.id}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }),
    onSuccess: () => {
      setStatus("success");
      setIsSplitIncidentModalOpen(false);
      setSplitFinalCost("");
      setSplitIncidentType("");
      setComplaint("");
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
      queryClient.invalidateQueries({ queryKey: ["repairs", incident.id] });
    },
    onError: (error) => {
      setStatus("error");
      setErrorMessage(
        error.message || "An error occurred while splitting the incident",
      );
    },
  });

  const handleSplitIncidentSubmit = () => {
    if (!splitFinalCost || !splitIncidentType) {
      setErrorMessage("Please fill in all fields");
      return;
    }

    const finalCostValue = parseFloat(splitFinalCost);
    if (isNaN(finalCostValue)) {
      setErrorMessage("Final Cost must be a valid number");
      return;
    }

    const data = {
      complaint: complaint,
      finalCost: finalCostValue,
      incidentType: splitIncidentType,
    };

    splitIncidentMutation.mutate(data);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth="xl">
        <DialogTitle>Repairs</DialogTitle>
        <DialogContent>
          <Box sx={{ height: 300, width: "100%" }}>
            {isLoading ? (
              <Box className="table-loader">
                <CircularProgress />
              </Box>
            ) : isError ? (
              <Box className="table-error">
                <Typography color="error">Error: {error.message}</Typography>
              </Box>
            ) : (
              <DataGrid
                rows={repairs || []}
                columns={createRepairsTableColumns(
                  handleShopNameClick,
                  setAiSplitRepair,
                )}
                processRowUpdate={(newRow, oldRow) =>
                  handleProcessRowUpdate(newRow, oldRow)
                }
                onProcessRowUpdateError={handleProcessRowUpdateError}
                getRowClassName={getRepairRowClassName}
              />
            )}
          </Box>
          <Button
            onClick={handleNewRepair}
            variant="contained"
            color="primary"
            sx={{ marginTop: 2 }}
          >
            New Repair
          </Button>
          <Button
            onClick={() => setIsSplitIncidentModalOpen(true)}
            variant="contained"
            color="primary"
            sx={{ marginTop: 2, marginLeft: 2 }}
          >
            Split Incident
          </Button>
          <RepairPopup
            isOpened={isNewRepair}
            setIsOpened={setIsNewRepair}
            incident={incident}
            affectedTruckId={affectedTruckId}
            affectedTrailerId={affectedTrailerId}
          />
          <Box sx={{ mt: 4 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Charge Incident
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isDriverCharged}
                      onChange={(e) =>
                        handleDriverChargeToggle(e.target.checked)
                      }
                      disabled={chargeSwitchDisabled}
                    />
                  }
                  label="Charge driver"
                />
                <TextField
                  label="Driver charged amount"
                  fullWidth
                  margin="dense"
                  value={chargedDriverAmount}
                  disabled={!isDriverCharged}
                  onChange={(e) => setChargedDriverAmount(e.target.value)}
                  placeholder="0.00"
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isCompanyCharged}
                      onChange={(e) =>
                        handleCompanyChargeToggle(e.target.checked)
                      }
                      disabled={chargeSwitchDisabled}
                    />
                  }
                  label="Charge company"
                />
                <Autocomplete
                  options={ownerCompanies || []}
                  getOptionLabel={(option) => option.label}
                  value={
                    selectedCompany
                      ? ownerCompanies?.find(
                          (company: { id: string }) =>
                            company.id === selectedCompany.id,
                        ) || selectedCompany
                      : null
                  }
                  onChange={(event, newValue) => setSelectedCompany(newValue)}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value?.id
                  }
                  disabled={!isCompanyCharged}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Company"
                      margin="dense"
                      fullWidth
                      disabled={!isCompanyCharged}
                    />
                  )}
                />
                <TextField
                  label="Company charged amount"
                  fullWidth
                  margin="dense"
                  value={chargedCompanyAmount}
                  disabled={!isCompanyCharged}
                  onChange={(e) => setChargedCompanyAmount(e.target.value)}
                  placeholder="0.00"
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={handleChargeSubmit}
                disabled={chargeSwitchDisabled || isChargeSubmitting}
              >
                {isChargeSubmitting ? "Saving..." : "Save Charges"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isReasonModalOpen}
        onClose={() => setIsReasonModalOpen(false)}
      >
        <DialogTitle>Enter On Hold Reason</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for holding repair"
            fullWidth
            variant="outlined"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsReasonModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReasonSubmit}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={isCompletionModalOpen} onClose={closeCompletionModal}>
        <DialogTitle>Completion Details</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Authorized By"
            fullWidth
            variant="outlined"
            value={completionAuthorizedBy}
            onChange={(e) => setCompletionAuthorizedBy(e.target.value)}
            helperText={
              !completionAuthorizedBy.length
                ? "Please enter the name of the person who authorized the repair completion"
                : ""
            }
          />
          <TextField
            margin="dense"
            label="Final Cost"
            fullWidth
            variant="outlined"
            value={completionFinalCost}
            onChange={(e) => {
              setCompletionFinalCost(e.target.value);
              setErrorMessage(null);
            }}
            error={!!errorMessage}
            helperText={errorMessage}
          />
          <TextField
            margin="dense"
            label="Completed By"
            fullWidth
            variant="outlined"
            value={completedBy}
            onChange={(e) => {
              setCompletedBy(e.target.value);
            }}
            helperText={
              !completedBy.length
                ? "Please enter the name of the person who completed the repair"
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCompletionModal}>Cancel</Button>
          <Button
            onClick={handleCompletionSubmit}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      {isUpdateIncidentModalOpen && (
        <GenericFormPopup
          formFields={getUpdateIncidentFromRepairTableFieldConfiguration(
            initialValues.assetAffected ?? incidentAssetAffected,
          )}
          initialValues={initialValues}
          title="Check or update incident's details"
          onSubmit={submitHandler}
          open={isUpdateIncidentModalOpen}
          onClose={closeUpdateIncidentModal}
          loading={incidentMutation.isPending}
        />
      )}
      <Dialog
        open={isSplitIncidentModalOpen}
        onClose={() => {
          setIsSplitIncidentModalOpen(false);
          setErrorMessage(null);
        }}
      >
        <DialogTitle>Split Incident</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Complaint"
            fullWidth
            variant="outlined"
            value={complaint}
            onChange={(e) => {
              setComplaint(e.target.value);
              setErrorMessage(null);
            }}
          />
          <TextField
            autoFocus
            margin="dense"
            label="Final Cost"
            fullWidth
            variant="outlined"
            value={splitFinalCost}
            onChange={(e) => {
              setSplitFinalCost(e.target.value);
              setErrorMessage(null);
            }}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="incident-type-label">Incident Type</InputLabel>
            <Select
              labelId="incident-type-label"
              id="incident-type-select"
              value={splitIncidentType}
              label="Incident Type"
              onChange={(e) => {
                setSplitIncidentType(e.target.value);
                setErrorMessage(null);
              }}
            >
              {getIncidentTypesForAsset(incidentAssetType)
                .filter(({ value }) => value)
                .map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          {errorMessage && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsSplitIncidentModalOpen(false);
              setErrorMessage(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSplitIncidentSubmit}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <FileUploadModal
        open={isInvoiceModalOpen}
        onClose={(currentRow: GridValidRowModel) =>
          handleInvoiceClose(currentRow)
        }
        row={currentRow}
        type="invoice"
        incident={incidentData}
      />
      <FileUploadModal
        open={isReceiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        row={currentRow}
        type="receipt"
      />
      <IncidentProofUploadModal
        open={isProofUploadModalOpen}
        onClose={() => setProofUploadModalOpen(false)}
        incident={incidentData}
      />
      <ShopDetailsPopup
        open={isShopDetailsModalOpen}
        onClose={() => setIsShopDetailsModalOpen(false)}
        details={shopDetails}
      />
      <StatusSnackbar
        status={status}
        successMessage="Row updated successfully!"
        errorMessage={errorMessage}
        handleClose={handleSnackbarClose}
      />
      {aiSplitRepair && (
        <AiRepairSplitsDialog
          open={!!aiSplitRepair}
          incident={incidentData}
          repair={aiSplitRepair}
          assetType={incidentAssetType}
          onClose={() => setAiSplitRepair(null)}
        />
      )}
    </>
  );
};

export default RepairsTable;
